import React from "react";
import { useParams, Link } from "react-router-dom"
import Slider from "react-slick"
import { FaBed, FaBath, FaRuler } from "react-icons/fa"
import { FaToilet, FaUtensils } from "react-icons/fa"
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css"
import usePublicProperty from "../hooks/usePublicProperty"

const carouselSettings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: true,
};

const renderHeroMedia = (property) => {
  const firstMedia = property.media[0];
  if (!firstMedia) {
    return <div className="absolute inset-0 bg-slate-700"></div>;
  }

  if (firstMedia.kind === "video") {
    return (
      <video
        src={firstMedia.url}
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 h-full w-full object-cover"
      />
    );
  }

  return (
    <img
      src={firstMedia.url}
      alt={`${property.title} hero`}
      className="absolute inset-0 h-full w-full object-cover"
    />
  );
};

const renderSlideMedia = (property, media, index) => {
  if (media.kind === "video") {
    return (
      <video
        src={media.url}
        controls
        className="w-full h-80 sm:h-96 object-cover"
        aria-label={`${property.title} video ${index + 1}`}
      />
    );
  }

  return (
    <img
      src={media.url}
      alt={`${property.title} view ${index + 1}`}
      className="w-full h-80 sm:h-96 object-cover"
    />
  );
};

const PropertyDetails = () => {
  const { id } = useParams();
  const { property, loading, error } = usePublicProperty(id);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <p className="text-gray-600">Loading property...</p>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-semibold text-gray-800">Property not found</h1>
          <p className="mt-2 text-gray-600">
            {error || "The property you are looking for does not exist."}
          </p>
          <Link
            to="/properties"
            className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-secondary"
          >
            Back to Properties Page
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen">
      <section className="relative h-72 sm:h-80 lg:h-96">
        {renderHeroMedia(property)}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-16 h-full flex flex-col justify-end pb-8">
          <Link to="/properties" className="text-sm text-white/90 hover:text-white">
            Back to Properties
          </Link>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            {property.title}
          </h1>
          <p className="mt-2 text-white/80">{property.location}</p>
          <div className="mt-4 inline-flex w-fit items-center rounded-full bg-white/15 px-4 py-2 text-sm text-white backdrop-blur">
            {new Intl.NumberFormat("en-NG", {
              style: "currency",
              currency: "NGN",
              maximumFractionDigits: 0,
            }).format(property.price)}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-16 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 xl:gap-12">
          <div className="lg:col-span-2 space-y-8 xl:space-y-10">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-aos="fade-up">
              <Slider {...carouselSettings}>
                {property.media.map((media, index) => (
                  <div key={media.id}>{renderSlideMedia(property, media, index)}</div>
                ))}
              </Slider>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6" data-aos="fade-up">
              <h2 className="text-xl font-semibold text-gray-900">Property Overview</h2>
              <p className="mt-3 text-gray-700 leading-relaxed">{property.description}</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-aos="fade-up">
              <div className="rounded-xl bg-white p-4 shadow-md">
                <div className="flex items-center space-x-2 text-secondary">
                  <FaBed />
                  <span className="text-sm text-gray-600">Bedrooms</span>
                </div>
                <p className="mt-2 text-lg font-semibold text-gray-900">{property.beds}</p>
              </div>
              <div className="rounded-xl bg-white p-4 shadow-md">
                <div className="flex items-center space-x-2 text-secondary">
                  <FaBath />
                  <span className="text-sm text-gray-600">Bathrooms</span>
                </div>
                <p className="mt-2 text-lg font-semibold text-gray-900">{property.baths}</p>
              </div>
              <div className="rounded-xl bg-white p-4 shadow-md">
                <div className="flex items-center space-x-2 text-secondary">
                  <FaToilet />
                  <span className="text-sm text-gray-600">Toilets</span>
                </div>
                <p className="mt-2 text-lg font-semibold text-gray-900">{property.toilets}</p>
              </div>
              <div className="rounded-xl bg-white p-4 shadow-md">
                <div className="flex items-center space-x-2 text-secondary">
                  <FaUtensils />
                  <span className="text-sm text-gray-600">Kitchen</span>
                </div>
                <p className="mt-2 text-lg font-semibold text-gray-900">{property.kitchens}</p>
              </div>
              <div className="rounded-xl bg-white p-4 shadow-md">
                <div className="flex items-center space-x-2 text-secondary">
                  <FaRuler />
                  <span className="text-sm text-gray-600">Size</span>
                </div>
                <p className="mt-2 text-lg font-semibold text-gray-900">{property.size} sqm</p>
              </div>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-2xl bg-white p-6 shadow-lg" data-aos="fade-left">
              <h3 className="text-lg font-semibold text-gray-900">Ready to view?</h3>
              <p className="mt-2 text-sm text-gray-600">
                Speak with our agent to arrange a private tour and get full pricing details.
              </p>
              <a
                href="https://wa.me/2348145765479"
                target="_blank"
                rel="noreferrer"
                className="mt-10 inline-flex w-full items-center justify-center rounded-md bg-primary px-5 py-3 text-sm font-semibold text-white hover:bg-secondary"
              >
                Contact Agent
              </a>
            </div>

            <div className="rounded-2xl bg-white p-6 shadow-lg" data-aos="fade-left">
              <h3 className="text-lg font-semibold text-gray-900">Highlights</h3>
              <ul className="mt-4 space-y-3 text-sm text-gray-700">
                <li>Secure neighborhood and gated access</li>
                <li>Dedicated parking and backup power</li>
                <li>Close to major roads and amenities</li>
                <li>Modern finishes and spacious layout</li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetails;
